import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { AmbientMesh } from '@/components/AmbientMesh';
import { Wordmark } from '@/components/Mark';
import { Surface } from '@/components/ui';

/**
 * Privacy Policy — linked from signup. Describes what data Poof actually
 * collects on the operator side (your account, your link config, your
 * dashboard analytics) AND what flows through Poof on behalf of your
 * visitors (request data used for fraud/bot filtering, optional Meta
 * CAPI forwarding, the per-link 18+ gate). EU visitors are forwarded
 * with Meta&rsquo;s Limited Data Use (LDU) flag set.
 *
 * Customer-facing copy describes outcomes (&ldquo;real humans only&rdquo;),
 * not mechanisms (no &ldquo;IAB escape&rdquo; or in-app-browser language).
 *
 * Like Terms, this is the founder&rsquo;s initial draft &mdash; review
 * by counsel before relying on it for a regulatory question.
 */
const LAST_UPDATED = '2026-05-19';

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <Section heading="Who we are">
        <p>
          Poof (&ldquo;we,&rdquo; &ldquo;us&rdquo;) operates the
          link-in-bio and analytics service at poof.bio. This policy
          covers the personal data we process about (a) you, our
          customer, and (b) visitors who click links you publish through
          the Service. Contact:{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>
          .
        </p>
      </Section>

      <Section heading="What we collect about you (the customer)">
        <ul className="ml-5 list-disc space-y-1">
          <li>Account: your name, email, and password (stored as a salted hash).</li>
          <li>
            Configuration: the links, rules, custom domains, and pages
            you create &mdash; this is your content, not a tracking
            signal.
          </li>
          <li>
            Billing (if you subscribe to a paid plan): handled by our
            payment processor; we receive plan status and the last four
            digits of the card, not the full number.
          </li>
          <li>
            Product analytics: which dashboard pages you visit and what
            actions you take, so we know what features are worth
            building.
          </li>
        </ul>
      </Section>

      <Section heading="What flows through Poof about your visitors">
        <p>
          When someone clicks a link you publish, our edge sees the
          standard HTTP request data: IP address, User-Agent, the
          referrer, the URL path, and timestamps. We use that data to:
        </p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            Filter out bots and other non-human traffic, so your click
            count and CAPI events reflect real humans.
          </li>
          <li>Show you aggregate analytics in your dashboard.</li>
          <li>
            Forward conversion events to <em>your</em> Meta Conversions
            API pixel, if you&rsquo;ve configured one. The payload
            includes the visitor&rsquo;s IP, User-Agent, hashed
            identifiers Meta expects, and any event parameters you set.
          </li>
        </ul>
        <p>
          We don&rsquo;t sell visitor data, we don&rsquo;t build a
          cross-customer profile of visitors, and we don&rsquo;t share
          one customer&rsquo;s analytics with another.
        </p>
      </Section>

      <Section heading="EU / UK visitors and Limited Data Use">
        <p>
          When we detect that a visitor is in the EU, UK, or another
          region where it&rsquo;s warranted, CAPI events forwarded to
          Meta are tagged with the Limited Data Use (LDU) flag. This
          tells Meta to process the event under stricter rules and not
          use it for ad personalization. Hashed identifiers are still
          sent so attribution works for the advertiser, consistent with
          Meta&rsquo;s documented LDU posture.
        </p>
        <p>
          You, as the customer publishing the link, are the data
          controller for the visitor data flowing to your CAPI pixel.
          Poof acts as a processor for that flow. We&rsquo;ll sign a
          standard data processing agreement on request &mdash; email{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>
          .
        </p>
      </Section>

      <Section heading="Cookies and the per-link 18+ gate">
        <p>
          Poof uses a small number of strictly-necessary cookies on the
          dashboard (session, CSRF) and on lander pages where you&rsquo;ve
          enabled the per-link 18+ age gate (to remember that this
          device passed the gate). We don&rsquo;t set advertising
          cookies on landers by default. If you turn on Meta pixel
          forwarding, the standard Meta pixel cookies may be set on
          domains you control &mdash; that&rsquo;s your pixel, on your
          domain, under your control.
        </p>
      </Section>

      <Section heading="Sub-processors">
        <ul className="ml-5 list-disc space-y-1">
          <li>Cloudflare (compute, CDN, R2 object storage, Turnstile bot challenge)</li>
          <li>Meta (when you forward events to your own CAPI pixel)</li>
          <li>Email + payment providers used for transactional mail and billing</li>
        </ul>
        <p>
          We&rsquo;ll keep this list current. Material additions will
          be announced before they go live so you can object.
        </p>
      </Section>

      <Section heading="Retention">
        <p>
          Account data is kept while your account is open. Per-click
          analytics are retained at full resolution for 90 days and in
          aggregated form thereafter. You can request deletion of your
          account and your raw analytics at any time by emailing{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>
          ; we&rsquo;ll act within 30 days, subject to legal retention
          obligations.
        </p>
      </Section>

      <Section heading="Your rights">
        <p>
          Depending on where you live, you may have the right to
          access, correct, port, or delete personal data we hold about
          you, and to object to or restrict certain processing. Email{' '}
          <a href="mailto:hello@poof.bio" className="underline">
            hello@poof.bio
          </a>{' '}
          and we&rsquo;ll respond within the timeframe your law
          requires. If we can&rsquo;t resolve a concern, you can
          complain to your local data protection authority.
        </p>
      </Section>

      <Section heading="Security">
        <p>
          We use TLS for all traffic, hash passwords, scope service
          credentials to least-privilege, and run our infrastructure on
          Cloudflare. No system is unbreakable; if a breach occurs that
          affects your data we&rsquo;ll notify you and the relevant
          authorities as the law requires.
        </p>
      </Section>

      <Section heading="Changes">
        <p>
          We&rsquo;ll update this policy as the Service evolves. The
          &ldquo;Last updated&rdquo; date above reflects the version in
          force. Material changes will be announced in-product or by
          email.
        </p>
      </Section>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Local presentational helpers, mirrored from Terms.tsx. Kept colocated
// because Terms + Privacy are the only legal pages today and a shared
// `LegalLayout` component would just hide one import behind another.
// ---------------------------------------------------------------------------

function LegalShell({
  title,
  lastUpdated,
  children,
}: {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}) {
  return (
    <div className="relative min-h-screen">
      <AmbientMesh />

      <div className="relative z-10 mx-auto max-w-3xl px-6 py-10 md:py-16">
        <header className="mb-10 flex items-center justify-between">
          <Wordmark to="/" size="md" />
          <RouterLink
            to="/login"
            className="text-sm text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
          >
            Sign in
          </RouterLink>
        </header>

        <Surface tone="raised" className="p-8 md:p-12">
          <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
            {title}
          </h1>
          <p className="mt-2 text-sm text-[var(--color-on-surface-variant)]">
            Last updated {lastUpdated}
          </p>
          <div className="mt-8 space-y-8 text-[var(--color-foreground)]">
            {children}
          </div>
        </Surface>

        <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 text-xs text-[var(--color-on-surface-variant)] opacity-70">
          <span>© 2026 Poof</span>
          <div className="flex gap-5">
            <RouterLink to="/terms" className="hover:text-[var(--color-foreground)]">
              Terms
            </RouterLink>
            <RouterLink to="/privacy" className="hover:text-[var(--color-foreground)]">
              Privacy
            </RouterLink>
            <a href="mailto:hello@poof.bio" className="hover:text-[var(--color-foreground)]">
              hello@poof.bio
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold tracking-tight">{heading}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
        {children}
      </div>
    </section>
  );
}
